import { PLATFORMS } from './constants.js';
/** Truncate text on a word boundary, appending an ellipsis when needed. */
export function truncate(text, max) {
    if (max <= 0)
        return '';
    if (text.length <= max)
        return text;
    const slice = text.slice(0, Math.max(1, max - 1));
    const boundary = slice.lastIndexOf(' ');
    const cut = boundary > max * 0.6 ? slice.slice(0, boundary) : slice;
    return `${cut.trimEnd()}…`;
}
export function countHashtags(text) {
    return (text.match(/(^|\s)#[\p{L}\p{N}_]+/gu) ?? []).length;
}
export function extractHashtags(text) {
    return Array.from(new Set((text.match(/(^|\s)(#[\p{L}\p{N}_]+)/gu) ?? []).map((m) => m.trim())));
}
export function ensureHashtagPrefix(tags) {
    return Array.from(new Set(tags
        .map((tag) => tag.trim().replace(/^#+/, '').replace(/\s+/g, ''))
        .filter(Boolean)
        .map((tag) => `#${tag}`)));
}
/**
 * Build the per-platform caption for a piece of content.
 * Keeps hashtags within the platform limit and truncates the body so the
 * final caption never exceeds the platform's hard character limit.
 */
export function buildCaption(body, hashtags, platform) {
    const descriptor = PLATFORMS[platform];
    const tags = ensureHashtagPrefix(hashtags).slice(0, descriptor.maxHashtags);
    const suffix = tags.length > 0 ? `\n\n${tags.join(' ')}` : '';
    const budget = descriptor.maxChars - suffix.length;
    const clipped = budget > 0 ? truncate(body.trim(), budget) : '';
    return `${clipped}${suffix}`.trim();
}
export function daysBetween(a, b) {
    const ms = Math.abs(a.getTime() - b.getTime());
    return Math.floor(ms / 86_400_000);
}
export function addDays(date, days) {
    const next = new Date(date.getTime());
    next.setUTCDate(next.getUTCDate() + days);
    return next;
}
/** Percent change from `previous` to `current`, guarding against divide-by-zero. */
export function percentChange(current, previous) {
    if (previous === 0) {
        return current === 0 ? 0 : 100;
    }
    return Number((((current - previous) / previous) * 100).toFixed(2));
}
export function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
export function formatCompact(value) {
    if (!Number.isFinite(value))
        return '0';
    return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}
export function slugify(value) {
    return value
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[^\p{L}\p{N}]+/gu, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80);
}
/** Deterministic pseudo-random generator so degraded AI output is reproducible in tests. */
export function seededRandom(seed) {
    let state = seed % 2147483647;
    if (state <= 0)
        state += 2147483646;
    return () => {
        state = (state * 16807) % 2147483647;
        return (state - 1) / 2147483646;
    };
}
/**
 * Format a credit balance for display (`1500` → `1,500 credits`).
 *
 * Credits are a whole-number promotional unit, deliberately *not* money: this
 * never renders a currency symbol, because a credit is not redeemable for cash.
 * Pass `singular`/`plural` so the label can be localised.
 */
export function formatCredits(credits, labels) {
    const safe = Number.isFinite(credits) ? Math.trunc(credits) : 0;
    const unit = Math.abs(safe) === 1 ? labels.singular : labels.plural;
    return `${safe.toLocaleString('en-IN')} ${unit}`;
}
